import { SystemState, ALERTS_VISIBILITY } from "../reducers/system";
import { Action } from "redux-first-router";
import config from '../../config.json'

export type SYSTEM_ACTION_TYPES = SetMobileAction | SetMobileMenuAction | ThemeAction | SetChannelAction | SetMessageClientStatusAction | SetAlertVisibilityAction | SetOauthNonceAction | SetDeferredDestinationAction | AcceptTermsAction | SetFilterMenuAction | SetDeviceIdAction | SetAppIsActive | SetAppKeyboardAction | SetWindowDimensionsAction;

export enum SYSTEM_ACTIONS {
  THEME_TOGGLED = "THEME_TOGGLED",
  THEME_SET = "THEME_SET",
  MOBILE_SET = "MOBILE_SET",
  MOBILE_MENU_OPENED = "MOBILE_MENU_OPENED",
  MOBILE_MENU_CLOSED = "MOBILE_MENU_CLOSED",
  CHANNEL_SET = "CHANNEL_SET",
  MESSAGE_CLIENT_STATUS_SET = "MESSAGE_CLIENT_STATUS_SET",
  ALERTS_VISIBILITY_SET = "ALERTS_VISIBILITY_SET",
  OAUTH_NONCE_SET = "OAUTH_NONCE_SET",
  DEFERRED_DESTINATION_SET = "DEFERRED_DESTINATION_SET",
  DEFERRED_DESTINATION_CLEARED = "DEFERRED_DESTINATION_CLEARED",
  TERMS_ACCEPTED = "TERMS_ACCEPTED",
  FILTER_MENU_OPENED = "FILTER_MENU_OPENED",
  FILTER_MENU_CLOSED = "FILTER_MENU_CLOSED",
  DEVICE_ID_SET = "DEVICE_ID_SET",
  APP_STATE_ACTIVATED = "APP_STATE_ACTIVATED",
  APP_STATE_DEACTIVATED = "APP_STATE_DEACTIVATED",
  APP_KEYBOARD_OPENED = "APP_KEYBOARD_OPENED",
  APP_KEYBOARD_CLOSED = "APP_KEYBOARD_CLOSED",
  WINDOW_DIMENSIONS_CHANGED = "WINDOW_DIMENSIONS_CHANGED"
}

export type SetMobileAction = {
  type: SYSTEM_ACTIONS.MOBILE_SET
  payload: boolean
}

export type SetMobileMenuAction = {
  type: SYSTEM_ACTIONS.MOBILE_MENU_CLOSED | SYSTEM_ACTIONS.MOBILE_MENU_OPENED
}

export type ThemeAction = {
  type: SYSTEM_ACTIONS.THEME_TOGGLED | SYSTEM_ACTIONS.THEME_SET,
  payload: "dark" | "light" | "paper" | undefined
}

export type SetChannelAction = {
  type: SYSTEM_ACTIONS.CHANNEL_SET
  payload: number
}

export type SetMessageClientStatusAction = {
  type: SYSTEM_ACTIONS.MESSAGE_CLIENT_STATUS_SET
  payload: SystemState["messageClientStatus"]
}

export type SetAlertVisibilityAction = {
  type: SYSTEM_ACTIONS.ALERTS_VISIBILITY_SET,
  payload: ALERTS_VISIBILITY
}

export type SetOauthNonceAction = {
  type: SYSTEM_ACTIONS.OAUTH_NONCE_SET,
  payload: string
}

export type SetDeferredDestinationAction = {
  type: SYSTEM_ACTIONS.DEFERRED_DESTINATION_SET | SYSTEM_ACTIONS.DEFERRED_DESTINATION_CLEARED,
  payload?: Action
}

export type AcceptTermsAction = {
  type: SYSTEM_ACTIONS.TERMS_ACCEPTED,
  payload: string
}

export type SetFilterMenuAction = {
  type: SYSTEM_ACTIONS.FILTER_MENU_CLOSED | SYSTEM_ACTIONS.FILTER_MENU_OPENED
}

export type SetDeviceIdAction = {
  type: SYSTEM_ACTIONS.DEVICE_ID_SET,
  payload: string
}

export type SetAppIsActive = {
  type: SYSTEM_ACTIONS.APP_STATE_ACTIVATED | SYSTEM_ACTIONS.APP_STATE_DEACTIVATED
}

export type SetAppKeyboardAction = {
  type: SYSTEM_ACTIONS.APP_KEYBOARD_OPENED | SYSTEM_ACTIONS.APP_KEYBOARD_CLOSED,
  payload?: number
}

export type SetWindowDimensionsAction = {
  type: SYSTEM_ACTIONS.WINDOW_DIMENSIONS_CHANGED,
  payload: SystemState["windowDims"]
}


export const doToggleTheme = () => {
  return {
    type: SYSTEM_ACTIONS.THEME_TOGGLED,
  }
}

export const doSetTheme = (theme: "dark" | "light" | "paper") => {
  return {
    type: SYSTEM_ACTIONS.THEME_SET,
    payload: theme
  }
}

export const doSetIsMobile = (value: boolean): SetMobileAction => {
  return {
    type: SYSTEM_ACTIONS.MOBILE_SET,
    payload: value
  }
}

export const doSetMobileMenuOpen = (isOpen: boolean): SetMobileMenuAction => {
  return {
    type: isOpen ? SYSTEM_ACTIONS.MOBILE_MENU_OPENED : SYSTEM_ACTIONS.MOBILE_MENU_CLOSED,
  }
}

export const doSetChannel = (channelId: number): SetChannelAction => {
  return {
    type: SYSTEM_ACTIONS.CHANNEL_SET,
    payload: channelId
  }
}

export const doSetMessageClientStatus = (status: SetMessageClientStatusAction["payload"]): SetMessageClientStatusAction => {
  return {
    type: SYSTEM_ACTIONS.MESSAGE_CLIENT_STATUS_SET,
    payload: status
  }
}

export const doSetAlertsVisibility = (visibility: ALERTS_VISIBILITY): SetAlertVisibilityAction => {
  return {
    type: SYSTEM_ACTIONS.ALERTS_VISIBILITY_SET,
    payload: visibility
  }
}

export const doSetOauthNonce = (nonce: string): SetOauthNonceAction => {
  return {
    type: SYSTEM_ACTIONS.OAUTH_NONCE_SET,
    payload: nonce
  }
}

export const doSetDeferredDestination = (action: Action) => {
  return {
    type: SYSTEM_ACTIONS.DEFERRED_DESTINATION_SET,
    payload: action
  }
}

export const doClearDeferredDestination = () => {
  return {
    type: SYSTEM_ACTIONS.DEFERRED_DESTINATION_CLEARED,
  }
}

export const doAcceptTerms = () => {
  return {
    type: SYSTEM_ACTIONS.TERMS_ACCEPTED,
    payload: config.TOS_VERSION
  }
}

export const doSetFilterMenuOpen = (isOpen: boolean): SetFilterMenuAction => {
  return {
    type: isOpen ? SYSTEM_ACTIONS.FILTER_MENU_OPENED : SYSTEM_ACTIONS.FILTER_MENU_CLOSED,
  }
}

// Sets the device ID on native apps
export const doSetDeviceId = (deviceId: string) => {
  return {
    type: SYSTEM_ACTIONS.DEVICE_ID_SET,
    payload: deviceId
  }
}

// Sets the app state
export const doSetAppIsActive = (isActive: boolean) => {
  return {
    type: isActive ? SYSTEM_ACTIONS.APP_STATE_ACTIVATED : SYSTEM_ACTIONS.APP_STATE_DEACTIVATED
  }
}

// Sets the app keyboard to open
export const doSetAppKeyboardOpen = (keyboardHeight: number) => {
  return {
    type: SYSTEM_ACTIONS.APP_KEYBOARD_OPENED,
    payload: keyboardHeight
  }
}

// Sets the app keyboard to closed
export const doSetAppKeyboardClosed = () => {
  return {
    type: SYSTEM_ACTIONS.APP_KEYBOARD_OPENED
  }
}

export const doSetWindowDims = (dims: SystemState["windowDims"]) => {
  return {
    type: SYSTEM_ACTIONS.WINDOW_DIMENSIONS_CHANGED,
    payload: dims
  }
}